import { Component, EventEmitter, Input, Output, OnInit, signal, inject } from '@angular/core';
import { Router } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { CookieService } from 'ngx-cookie-service';
import { ToastService } from 'angular-toastify';
import { GroupSubMenu } from '../../../../../core/models/system-settings/group-sub-menus.model';
import { GroupMenuOption, GroupMenuDto } from '../../../../../core/models/system-settings/group-menus.model';
import { ApiSystemSettingsService } from '../../../../../core/services/api/system-settings/api-system-settings.service';
import { MapperGroupSubMenusService } from '../../../../../core/mappers/system-settings/mapper-group-sub-menus.service';
import { MenuPermissionService } from '../../../../../core/services/menu-permission.service';
import { ModalAdminActivityHistoryComponent } from '../modal-admin-activity-history/modal-admin-activity-history.component';
import { AdminActivityEntityTypes } from '../../../../../core/models/system-settings/admin-activity-log.model';

@Component({
  selector: 'app-modal-group-sub-menu',
  standalone: true,
  imports: [CommonModule, FormsModule, ModalAdminActivityHistoryComponent],
  templateUrl: './modal-group-sub-menu.component.html',
  styleUrl: './modal-group-sub-menu.component.css'
})
export class ModalGroupSubMenuComponent implements OnInit {
  @Input() data: GroupSubMenu | null = null;
  @Output() close = new EventEmitter<void>();
  @Output() saveSuccess = new EventEmitter<void>();

  readonly activityEntityTypes = AdminActivityEntityTypes;
  showActivityHistory = false;

  formData: Partial<GroupSubMenu> = {
    id: 0,
    name: '',
    enable: true,
    groupMenuId: 0,
    groupMenuName: '',
    number: 1,
    icon: ''
  };

  groupMenuOptions = signal<GroupMenuOption[]>([]);
  isLoading = signal<boolean>(false);
  openDropdown: string | null = null;

  // Search autocomplete field (complying with Rule 4)
  searchGroupMenuText: string = '';

  // ─── Dependencies ───
  private toast = inject(ToastService);
  private apiService = inject(ApiSystemSettingsService);
  private mapper = inject(MapperGroupSubMenusService);
  private cookieService = inject(CookieService);
  private router = inject(Router);
  private permissionService = inject(MenuPermissionService);

  // ================= Normal Functions =================

  constructor() { }

  ngOnInit() {
    this.getGroupMenuOptions();
    if (this.data) {
      this.formData = { ...this.data };
      this.searchGroupMenuText = this.formData.groupMenuName || '';
    }
  }

  /**
   * Safe dropdown closing with validation and revert mechanism.
   * Compares typed text against available parent options.
   */
  closeDropdowns(): void {
    if (this.openDropdown === 'groupMenu') {
      const options = this.groupMenuOptions() || [];
      const match = options.find(
        (o) => o?.name?.toLowerCase() === this.searchGroupMenuText?.trim()?.toLowerCase()
      );

      if (match) {
        this.formData.groupMenuId = match.id;
        this.formData.groupMenuName = match.name;
        this.searchGroupMenuText = match.name;
      } else {
        // Revert to previously saved value
        this.searchGroupMenuText = this.formData.groupMenuName || '';
      }
    }
    this.openDropdown = null;
  }

  /**
   * Filters the dropdown options based on the typed search text.
   */
  get filteredGroupMenuOptions(): GroupMenuOption[] {
    const query = (this.searchGroupMenuText || '').toLowerCase().trim();
    const options = this.groupMenuOptions() || [];
    if (!query) return options;
    return options.filter(o => o?.name?.toLowerCase().includes(query));
  }

  /**
   * Opens the targeted dropdown.
   * @param menu Dropdown identifier
   */
  onInputFocus(menu: string): void {
    if (this.openDropdown && this.openDropdown !== menu) {
      this.closeDropdowns();
    }
    this.openDropdown = menu;
  }

  /**
   * Handles click inside the overlay.
   * @param event The mouse click event
   */
  onClickInsideModal(event: Event): void {
    event.stopPropagation();
    this.closeDropdowns();
  }

  /**
   * Toggles dropdown menu.
   * @param dropdown Selected dropdown identifier
   * @param event The click event
   */
  toggleDropdown(dropdown: string, event: Event): void {
    event.stopPropagation();
    if (this.openDropdown === dropdown) {
      this.closeDropdowns();
    } else {
      if (this.openDropdown) this.closeDropdowns();
      this.openDropdown = dropdown;
    }
  }

  /**
   * Saves selection.
   * @param opt Selected group menu option
   */
  selectGroupMenu(opt: GroupMenuOption): void {
    this.formData.groupMenuId = opt.id;
    this.formData.groupMenuName = opt.name;
    this.searchGroupMenuText = opt.name;
    this.openDropdown = null;
  }

  onClose() {
    this.close.emit();
  }

  /**
   * Validates input values and updates/saves submenu record.
   */
  onSave() {
    this.closeDropdowns();

    if (!this.formData.name || !this.formData.groupMenuId || this.formData.number === undefined) {
      this.toast.error('กรุณากรอกข้อมูลให้ครบถ้วนและเลือกกลุ่มเมนูหลักให้ถูกต้อง');
      return;
    }

    this.isLoading.set(true);

    // Extract user ID from cookie
    const userIdStr = this.cookieService.get('user_id');
    const userId = userIdStr ? parseInt(userIdStr, 10) : 0;

    // Get menuId from the current route
    const currentPath = this.router.url;
    const menuId = this.permissionService.getMenuId(currentPath) || 15; // Fallback to 15 if not found

    const payload = this.mapper.mapGroupSubMenuToPayload(this.formData as GroupSubMenu, userId, menuId);

    if (this.formData.id) {
      this.apiService.updateGroupSubMenu(payload, this.formData.id).subscribe({
        next: (res: any) => {
          console.log("update data : ", res, JSON.stringify(res));
          if (res?.isSuccess) {
            this.toast.success('แก้ไขข้อมูลสำเร็จ');
            this.saveSuccess.emit();
            this.close.emit();
          } else {
            this.toast.error(res?.message || 'แก้ไขข้อมูลไม่สำเร็จ');
          }
          this.isLoading.set(false);
        },
        error: (err: any) => {
          console.error(err);
          this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
          this.isLoading.set(false);
        }
      });
    } else {
      this.apiService.createGroupSubMenu(payload).subscribe({
        next: (res: any) => {
          console.log("create data : ", res, JSON.stringify(res));
          if (res?.isSuccess) {
            this.toast.success('สร้างข้อมูลสำเร็จ');
            this.saveSuccess.emit();
            this.close.emit();
          } else {
            this.toast.error(res?.message || 'สร้างข้อมูลไม่สำเร็จ');
          }
          this.isLoading.set(false);
        },
        error: (err: any) => {
          console.error(err);
          this.toast.error('เกิดข้อผิดพลาดในการเชื่อมต่อเซิร์ฟเวอร์');
          this.isLoading.set(false);
        }
      });
    }
  }

  // ================= API Functions =================

  getGroupMenuOptions(): void {
    this.apiService.getGroupMenusForTable().subscribe({
      next: (data: GroupMenuDto[]) => {
        const options = data.map(item => ({
          id: item.id,
          name: item.name
        }));
        this.groupMenuOptions.set(options || []);
      },
      error: () => {
        const fallbackOptions = [
          { id: 1, name: 'HOME' },
          { id: 2, name: 'PARKING SYSTEM' },
          { id: 3, name: 'SETTINGS' },
          { id: 4, name: 'อื่นๆ' }
        ];
        this.groupMenuOptions.set(fallbackOptions);
      }
    });
  }

  get canShowActivityHistory(): boolean {
    return !!this.formData.id && this.formData.id > 0;
  }

  openActivityHistory(): void {
    if (!this.canShowActivityHistory) return;
    this.showActivityHistory = true;
  }

  closeActivityHistory(): void {
    this.showActivityHistory = false;
  }
}
